import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const DAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];
const HOUR_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

interface ScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with an ISO-8601 string when the user confirms the schedule */
  onSchedule: (publishAt: string) => void;
  isLoading?: boolean;
  /**
   * ISO-8601 string of an existing scheduled date to pre-populate the modal
   * (e.g. when rescheduling). When omitted the modal defaults to the current
   * date and time so the user can adjust from there.
   */
  initialDate?: string;
}

// Helpers ──────────────────────────────────────────────────────────────────────

/** Convert a 24-hour value to a 12-hour value (1–12). */
function toHour12(hour24: number): number {
  if (hour24 === 0) return 12;
  if (hour24 > 12) return hour24 - 12;
  return hour24;
}

interface ParsedDateTime {
  viewYear: number;
  viewMonth: number;
  selectedDate: Date;
  selectedHour: number;
  selectedMinute: number;
  selectedPeriod: 'AM' | 'PM';
}

/**
 * Derive calendar and time-picker state from an existing ISO date string or
 * the current moment (for first-time scheduling).
 */
function parseDateTimeState(initialDate?: string): ParsedDateTime {
  const source = initialDate ? new Date(initialDate) : new Date();
  const h24 = source.getHours();
  return {
    viewYear: source.getFullYear(),
    viewMonth: source.getMonth(),
    selectedDate: new Date(source.getFullYear(), source.getMonth(), source.getDate()),
    selectedHour: toHour12(h24),
    selectedMinute: source.getMinutes(),
    selectedPeriod: h24 >= 12 ? 'PM' : 'AM',
  };
}

// Calendar helpers ─────────────────────────────────────────────────────────────

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Returns the 0-based Monday-first day offset for the first day of the month.
 * Monday = 0, Tuesday = 1, …, Sunday = 6.
 */
function getMondayFirstOffset(year: number, month: number): number {
  const jsDay = new Date(year, month, 1).getDay(); // 0 = Sun
  return jsDay === 0 ? 6 : jsDay - 1;
}

// Component ────────────────────────────────────────────────────────────────────

export function ScheduleModal({
  isOpen,
  onClose,
  onSchedule,
  isLoading,
  initialDate,
}: ScheduleModalProps) {
  const today = new Date();

  // Calendar state — initialised from initialDate or current time
  const [viewYear, setViewYear] = useState(() => parseDateTimeState(initialDate).viewYear);
  const [viewMonth, setViewMonth] = useState(() => parseDateTimeState(initialDate).viewMonth);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    () => parseDateTimeState(initialDate).selectedDate,
  );

  // Time state
  const [selectedHour, setSelectedHour] = useState(
    () => parseDateTimeState(initialDate).selectedHour,
  );
  const [selectedMinute, setSelectedMinute] = useState(
    () => parseDateTimeState(initialDate).selectedMinute,
  );
  const [selectedPeriod, setSelectedPeriod] = useState<'AM' | 'PM'>(
    () => parseDateTimeState(initialDate).selectedPeriod,
  );

  // Re-initialise whenever the modal opens or the seed date changes (e.g. rescheduling)
  useEffect(() => {
    if (!isOpen) return;
    const parsed = parseDateTimeState(initialDate);
    setViewYear(parsed.viewYear);
    setViewMonth(parsed.viewMonth);
    setSelectedDate(parsed.selectedDate);
    setSelectedHour(parsed.selectedHour);
    setSelectedMinute(parsed.selectedMinute);
    setSelectedPeriod(parsed.selectedPeriod);
  }, [isOpen, initialDate]);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  // Dismiss on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // ── Calendar helpers ────────────────────────────────────────────────────────

  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const isPastDate = (day: number): boolean => {
    return new Date(viewYear, viewMonth, day) < todayMidnight;
  };

  const isTodayDate = (day: number): boolean => {
    return (
      day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear()
    );
  };

  const isSelectedDate = (day: number): boolean => {
    return (
      selectedDate !== null &&
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === viewMonth &&
      selectedDate.getFullYear() === viewYear
    );
  };

  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewYear((y) => y - 1);
      setViewMonth(11);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewYear((y) => y + 1);
      setViewMonth(0);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const handleSelectDate = (day: number) => {
    if (isPastDate(day)) return;
    setSelectedDate(new Date(viewYear, viewMonth, day));
  };

  // Build grid: leading nulls for offset, then day numbers, pad to full weeks
  const startOffset = getMondayFirstOffset(viewYear, viewMonth);
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const cells: (number | null)[] = [
    ...Array<null>(startOffset).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  // ── Time helpers ────────────────────────────────────────────────────────────

  /** Converts 12-hour + AM/PM to 24-hour value */
  const toHour24 = (hour: number, period: 'AM' | 'PM'): number => {
    if (period === 'AM') return hour === 12 ? 0 : hour;
    return hour === 12 ? 12 : hour + 12;
  };

  // ── Validation ───────────────────────────────────────────────────────────────

  const scheduledDateTime: Date | null = (() => {
    if (!selectedDate) return null;
    return new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      toHour24(selectedHour, selectedPeriod),
      selectedMinute,
      0,
      0,
    );
  })();

  const isInPast = scheduledDateTime !== null && scheduledDateTime <= new Date();
  const canSchedule = scheduledDateTime !== null && !isInPast && !isLoading;

  // ── Submit ───────────────────────────────────────────────────────────────────

  const handleSchedule = () => {
    if (!canSchedule || !scheduledDateTime) return;
    onSchedule(scheduledDateTime.toISOString());
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
        aria-hidden="true"
        data-testid="schedule-modal-backdrop"
      />

      {/* Modal panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-modal-title"
        className="relative bg-card w-full max-w-[680px] rounded-2xl p-8 shadow-xl"
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2
              id="schedule-modal-title"
              className="text-lg font-bold text-foreground leading-tight"
            >
              Schedule this post
            </h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Choose when to publish your post.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 -mt-1 -mr-2 text-muted-foreground hover:text-foreground transition-colors rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body — two-column grid */}
        <div className="grid grid-cols-5 gap-6">
          {/* ── Left column: Calendar (3/5 ≈ 60%) ── */}
          <div className="col-span-3 border border-border rounded-xl overflow-hidden">
            {/* Month navigation */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <button
                type="button"
                onClick={goToPrevMonth}
                aria-label="Previous month"
                className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm font-semibold text-foreground">
                {MONTH_NAMES[viewMonth]} {viewYear}
              </span>
              <button
                type="button"
                onClick={goToNextMonth}
                aria-label="Next month"
                className="h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Day-of-week headers */}
            <div className="grid grid-cols-7 text-center px-3 pt-3 pb-1">
              {DAY_LABELS.map((label) => (
                <span key={label} className="text-xs font-medium text-muted-foreground">
                  {label}
                </span>
              ))}
            </div>

            {/* Date grid */}
            <div className="grid grid-cols-7 gap-y-1 px-3 pb-3">
              {cells.map((day, idx) => {
                if (day === null) {
                  return <div key={`empty-${idx}`} aria-hidden="true" />;
                }
                const past = isPastDate(day);
                const isToday = isTodayDate(day);
                const isSelected = isSelectedDate(day);
                return (
                  <button
                    key={day}
                    type="button"
                    disabled={past}
                    onClick={() => handleSelectDate(day)}
                    aria-label={`${day} ${MONTH_NAMES[viewMonth]} ${viewYear}${isToday ? ' (today)' : ''}`}
                    aria-pressed={isSelected}
                    className={cn(
                      'h-8 w-8 mx-auto flex items-center justify-center rounded-full text-sm transition-colors',
                      past && 'text-muted-foreground/40 cursor-not-allowed',
                      !past && !isSelected && 'text-foreground hover:bg-muted',
                      isToday && !isSelected && 'ring-1 ring-border font-medium',
                      isSelected && 'bg-foreground text-background font-medium',
                    )}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Right column: Time Picker (2/5 ≈ 40%) ── */}
          <div className="col-span-2 space-y-4">
            {/* AM / PM toggle */}
            <div>
              <div className="flex gap-2">
                {(['AM', 'PM'] as const).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setSelectedPeriod(p)}
                    aria-pressed={selectedPeriod === p}
                    className={cn(
                      'flex-1 py-1.5 rounded-md text-sm font-medium border transition-colors',
                      selectedPeriod === p
                        ? 'bg-foreground text-background border-foreground'
                        : 'border-border text-muted-foreground hover:text-foreground hover:border-foreground/30',
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-row gap-2">
              {/* Hour list */}
              <div className="w-full">
                <p className="text-xs text-muted-foreground mb-1.5">Hour</p>
                <div className="h-50 overflow-y-auto rounded-lg border border-border p-1 space-y-0.5">
                  {HOUR_OPTIONS.map((h) => (
                    <button
                      key={h}
                      type="button"
                      onClick={() => setSelectedHour(h)}
                      aria-pressed={selectedHour === h}
                      className={cn(
                        'w-full text-sm py-1.5 rounded-md text-center transition-colors',
                        selectedHour === h
                          ? 'bg-foreground text-background font-medium'
                          : 'text-foreground hover:bg-muted',
                      )}
                    >
                      {String(h).padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Minute list */}
              <div className="w-full">
                <p className="text-xs text-muted-foreground mb-1.5">Minute</p>
                <div className="max-h-50 h-50 overflow-y-auto rounded-lg border border-border p-1 space-y-0.5">
                  {Array.from({ length: 59 }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setSelectedMinute(i)}
                      aria-pressed={selectedMinute === i}
                      className={cn(
                        'w-full text-sm py-1.5 rounded-md text-center transition-colors',
                        selectedMinute === i
                          ? 'bg-foreground text-background font-medium'
                          : 'text-foreground hover:bg-muted',
                      )}
                    >
                      {String(i).padStart(2, '0')}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Inline past-date error */}
            {isInPast && (
              <p className="text-xs text-destructive" role="alert">
                The selected date and time are in the past.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
          <Button variant="ghost" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSchedule} disabled={!canSchedule} variant="outline" size="sm">
            {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Schedule
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
